import {CommonWorktag, UpdateCommonWorktag} from '~/types/common-worktag-type';
import {ActionCreatorWithPayload, createAction, createSlice, Draft, PayloadAction} from "@reduxjs/toolkit";

export interface WorktagState {
    worktags: string[],
    existingCommonWorktags: CommonWorktag[],
    currentCommonWorktags: CommonWorktag[],
    currentCommonWorktagIdx: number,
    meta: {
        loadedWorktags: boolean,
        fetchingCommonWorktags: boolean,
        loadedCommonWorktags: boolean,
        commonWorktagsDirty: boolean,
    },
}

export const initialState: WorktagState = {
    worktags: [],
    existingCommonWorktags: [],
    currentCommonWorktags: [],
    currentCommonWorktagIdx: 0,
    meta: {
        loadedWorktags: false,
        fetchingCommonWorktags: false,
        loadedCommonWorktags: false,
        commonWorktagsDirty: false,
    },
};

const findIndexByKey = (currentCommonWorktags: CommonWorktag[], key: number) => currentCommonWorktags
    .findIndex(commonWorktag => commonWorktag.key === key);

const transformCommonWorktags = (data: CommonWorktag[]) => {
    let idx = 0;
    const newCommonWorktags: CommonWorktag[] = data.map((row: CommonWorktag) => {
        idx += 1;
        return {
            key: idx,
            id: row.id,
            code: row.code,
            required: row.required,
            editable: row.editable,
        };
    });
    return {
        currentCommonWorktagIdx: idx,
        existingCommonWorktags: newCommonWorktags,
        currentCommonWorktags: newCommonWorktags
    };
};

const slice = createSlice({
        name: 'worktags',
        initialState: initialState,
        reducers: {
            loadWorktags: (draft: Draft<WorktagState>) => {
                draft.meta.loadedWorktags = false;
            },
            receiveWorktags: (draft: Draft<WorktagState>, {payload}: PayloadAction<string[]>) => {
                draft.meta.loadedWorktags = true;
                draft.worktags = payload;
            },
            receiveWorktagsFailed: (draft: Draft<WorktagState>) => {
                draft.meta.loadedWorktags = true;
            },
            loadCommonWorktags: (draft: Draft<WorktagState>) => {
                draft.meta.loadedCommonWorktags = false;
                draft.meta.fetchingCommonWorktags = true;
            },
            receiveCommonWorktags: (draft: Draft<WorktagState>, {payload}: PayloadAction<CommonWorktag[]>) => {
                const {
                    currentCommonWorktagIdx,
                    existingCommonWorktags,
                    currentCommonWorktags,
                } = transformCommonWorktags(payload);

                draft.meta.loadedCommonWorktags = true;
                draft.meta.fetchingCommonWorktags = false;
                draft.currentCommonWorktags = currentCommonWorktags;
                draft.existingCommonWorktags = existingCommonWorktags;
                draft.currentCommonWorktagIdx = currentCommonWorktagIdx;
            },
            receiveCommonWorktagsFailed: (draft: Draft<WorktagState>) => {
                draft.meta.loadedWorktags = true; // TODO: why is this not loadedCommonWorktags?
                draft.meta.fetchingCommonWorktags = false;
                draft.meta.commonWorktagsDirty = false;
            },
            addNewCommonWorktag: (draft: Draft<WorktagState>) => {
                const currentCommonWorktagIdx: number = draft.currentCommonWorktagIdx;
                const idx: number = currentCommonWorktagIdx + 1;
                const newCommonWorktag: CommonWorktag = {
                    key: idx,
                    code: '',
                    required: false,
                    editable: true
                };

                draft.currentCommonWorktagIdx = idx;
                draft.currentCommonWorktags.push(newCommonWorktag);
                draft.meta.commonWorktagsDirty = true;
            },
            commonWorktagsSaved: (draft: Draft<WorktagState>) => {
                draft.meta.commonWorktagsDirty = false;
            },
            setCommonWorktag: (draft: Draft<WorktagState>, {payload}: PayloadAction<UpdateCommonWorktag>) => {
                const idx = findIndexByKey(draft.currentCommonWorktags, payload.key);
                if (idx > -1) {
                    draft.currentCommonWorktags[idx].code = payload.code;
                    draft.meta.commonWorktagsDirty = true;
                }
            },
            setRequiredForCommonWorktag: (draft: Draft<WorktagState>, {payload}: PayloadAction<UpdateCommonWorktag>) => {
                const idx = findIndexByKey(draft.currentCommonWorktags, payload.key);
                if (idx > -1) {
                    draft.currentCommonWorktags[idx].required = payload.required;
                    draft.meta.commonWorktagsDirty = true;
                }
            },
            setCanEditForCommonWorktag: (draft: Draft<WorktagState>, {payload}: PayloadAction<UpdateCommonWorktag>) => {
                const idx = findIndexByKey(draft.currentCommonWorktags, payload.key);
                if (idx > -1) {
                    draft.currentCommonWorktags[idx].editable = payload.editable;
                    draft.meta.commonWorktagsDirty = true;
                }
            },
            deleteCommonWorktag: (draft: Draft<WorktagState>, {payload}: PayloadAction<number>) => {
                const idx = findIndexByKey(draft.currentCommonWorktags, payload);
                if (idx > -1) {
                    draft.currentCommonWorktags[idx].toDelete = true;
                    draft.meta.commonWorktagsDirty = true;
                }
            },
            undeleteCommonWorktag: (draft: Draft<WorktagState>, {payload}: PayloadAction<number>) => {
                const idx = findIndexByKey(draft.currentCommonWorktags, payload);
                if (idx > -1) {
                    draft.currentCommonWorktags[idx].toDelete = false;
                    draft.meta.commonWorktagsDirty = true;
                }
            }
        }
    }
);

const saveCommonWorktags: ActionCreatorWithPayload<any> = createAction('worktags/saveCommonWorktags');

export const actions = {
    ...slice.actions,
    saveCommonWorktags
};

export default slice.reducer;

